import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { INewsChatViewDTO } from '../../libs/coreapi-dto/service/newsChat';
import { MAX_NEWS_HISTORY_COUNT } from '../../constants';
import { DateTime } from 'luxon';

export interface NewsItem extends INewsChatViewDTO {
    readAt?: number; // timestamp когда прочитано
    receivedAt: number; // timestamp когда получено
}

interface NewsContextProps {
    news: NewsItem[];
    unreadCount: number;
    addNews: (newsItems: INewsChatViewDTO[]) => void;
    markAsViewed: (idGlobal: string) => void;
    markAllAsViewed: () => void;
    clearHistory: () => void;
    removeFromHistory: (idGlobal: string) => void;
    updateNews: (newsItems: INewsChatViewDTO[]) => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
}

const NewsContext = createContext<NewsContextProps | undefined>(undefined);

// Константа для хранения в localStorage
const NEWS_STORAGE_KEY = 'newsHistory';


export const NewsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [news, setNews] = useState<NewsItem[]>(() => {
        // Загружаем из localStorage при инициализации
        const saved = localStorage.getItem(NEWS_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Конвертируем строковые даты обратно в числа
                return parsed.map((item: any) => ({
                    ...item,
                    receivedAt: item.receivedAt,
                    readAt: item.readAt
                }));
            } catch (e) {
                console.error('Failed to parse news from localStorage:', e);
                return [];
            }
        }
        return [];
    });

    const [isLoading, setIsLoading] = useState(false);

    // Подсчет непрочитанных новостей
    const unreadCount = news.filter(item => !item.isViewed).length;

    // Сохраняем в localStorage при изменении
    useEffect(() => {
        localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(news));
    }, [news]);

    const getTimestamp = useCallback((startDate: string | DateTime | null | undefined): number => {
        if (!startDate) return 0;
        if (typeof startDate === 'string') {
            const dt = DateTime.fromISO(startDate);
            return dt.isValid ? dt.toMillis() : 0;
        }
        if (DateTime.isDateTime(startDate)) {
            return startDate.isValid ? startDate.toMillis() : 0;
        }
        return 0;
    }, []);

    // Добавление новых новостей (из API)
    const addNews = useCallback((newsItems: INewsChatViewDTO[]) => {
        setNews(prevNews => {
            const existingIds = new Set(prevNews.map(item => item.idGlobal));

            // Создаем новые элементы с метаданными
            const newItems: NewsItem[] = newsItems
                .filter(item => !existingIds.has(item.idGlobal))
                .map(item => ({
                    ...item,
                    receivedAt: DateTime.now().toMillis(),
                    readAt: item.isViewed ? DateTime.now().toMillis() : undefined
                }));

            // Объединяем старые и новые, сортируем по дате (сначала новые)
            const allNews = [...newItems, ...prevNews]
                .sort((a, b) => {
                    const timeA = getTimestamp(a.startDate) || a.receivedAt;
                    const timeB = getTimestamp(b.startDate) || b.receivedAt;
                    return timeB - timeA;
                })
                .slice(0, MAX_NEWS_HISTORY_COUNT);

            return allNews;
        });
    }, []);

    // Обновление существующих новостей (синхронизация с сервером)
    const updateNews = useCallback((newsItems: INewsChatViewDTO[]) => {
        setNews(prevNews => {
            const newsMap = new Map(prevNews.map(item => [item.idGlobal, item]));

            newsItems.forEach(item => {
                const existing = newsMap.get(item.idGlobal);
                if (existing) {
                    // Обновляем существующую новость
                    newsMap.set(item.idGlobal, {
                        ...existing,
                        ...item,
                        // Сохраняем оригинальный receivedAt если он был
                        receivedAt: existing.receivedAt,
                        // Если новость помечена как прочитанная на сервере, обновляем readAt
                        readAt: item.isViewed && !existing.isViewed ? Date.now() : existing.readAt
                    });
                } else {
                    // Добавляем новую
                    newsMap.set(item.idGlobal, {
                        ...item,
                        receivedAt: Date.now(),
                        readAt: item.isViewed ? Date.now() : undefined
                    });
                }
            });

            const allNews = Array.from(newsMap.values())
                .sort((a, b) => {
                    const timeA = getTimestamp(a.startDate) || a.receivedAt;
                    const timeB = getTimestamp(b.startDate) || b.receivedAt;
                    return timeB - timeA;
                })
                .slice(0, MAX_NEWS_HISTORY_COUNT);

            return allNews;
        });
    }, []);

    // Отметка о прочтении
    const markAsViewed = useCallback(async (idGlobal: string) => {
        setNews(prevNews =>
            prevNews.map(item =>
                item.idGlobal === idGlobal && !item.isViewed
                    ? {
                        ...item,
                        isViewed: true,
                        readAt: Date.now()
                    }
                    : item
            )
        );
    }, []);

    // Отметить все как прочитанные
    const markAllAsViewed = useCallback(() => {
        setNews(prevNews =>
            prevNews.map(item =>
                !item.isViewed
                    ? {
                        ...item,
                        isViewed: true,
                        readAt: Date.now()
                    }
                    : item
            )
        );
    }, []);

    // Очистить всю историю
    const clearHistory = useCallback(() => {
        setNews([]);
        localStorage.removeItem(NEWS_STORAGE_KEY);
    }, []);

    // Удалить конкретную новость из истории
    const removeFromHistory = useCallback((idGlobal: string) => {
        setNews(prevNews => prevNews.filter(item => item.idGlobal !== idGlobal));
    }, []);

    return (
        <NewsContext.Provider value={{
            news,
            unreadCount,
            addNews,
            markAsViewed,
            markAllAsViewed,
            clearHistory,
            removeFromHistory,
            updateNews,
            isLoading,
            setIsLoading
        }}>
            {children}
        </NewsContext.Provider>
    );
};

export const useNews = () => {
    const ctx = useContext(NewsContext);
    if (!ctx) throw new Error("useNews must be used inside NewsProvider");
    return ctx;
};